<template>
  <div>
    <div v-if="pageLoading">
      <SummarySkeletonLoader
        :is-loading="pageLoading"
        split-apart
        :number-of-metrics="4"
      />
      <TableSkeleton :table-title="labelsForLoading?.tableLabel || ''" />
    </div>
    <div v-else>
      <MetricsSummary
        height="135px"
        width="100%"
        label-width="90px"
        :summaries="summaries"
        new-version
      />

      <section class="mt-8" aria-labelledby="published-jobs-heading">
        <ChartCard class="snapshot-card" id-for-aria="published-jobs">
          <h2 id="published-jobs-heading" class="text-h2 pt-4 pl-4">
            Published Jobs Snapshot
          </h2>

          <transition name="fade">
            <div
              v-if="canRightScroll"
              class="scroll-side right-side-fade"
            />
          </transition>
          <WeeklySnapshotTable class="pl-4 pr-4" />
        </ChartCard>
        <Pagination
          v-if="totalPages > 1"
          :model-value="page"
          :length="totalPages"
          @update:model-value="(value) => sendFilterRequest({ pageNumber: value, router: $router })"
        />
      </section>
    </div>
  </div>
</template>

<script>
import { mapState, mapActions } from 'pinia';
import SummarySkeletonLoader from '@/components/shared/SkeletonLoaders/SummarySkeletonLoader.vue';
import TableSkeleton from '@/components/insights/skeletonLoaders/TableSkeleton.vue';
import MetricsSummary from '@/components/shared/MetricsSummary.vue';
import Pagination from '@/components/shared/Pagination.vue';
import { getQueryParamsFromURL } from '@/helpers/routing';
import WeeklySnapshotTable from '@/components/insights/OperationalInsights/WeeklySnapshot/WeeklySnapshotTable.vue';
import ChartCard from '@/components/insights/Chart/ChartCard.vue';
import { numberWithCommas } from '@/helpers/formatters';
import { useAppLoading } from '@/stores/app/loading';
import { useAppPagination } from '@/stores/app/pagination';
import { useWeeklySnapshot } from '@/stores/insights/weeklySnapshot';
import { useAppBase } from '@/stores/app/base';
import { useFilters } from '@/stores/filters/filters';
import { useDatePicker } from '@/stores/filters/datePicker';
import { subscribeToFilterUpdates, unsubscribeToFilterUpdates } from '@/helpers/filterDispatcher';
import { nextTick } from 'vue';
import { useColors } from '@/composables/colors';
import { useInsights } from '@/stores/insights/insights';
import { useInsightsExport } from '@/stores/insights/export';
import { useInsightsShare } from '@/stores/insights/share';

export default {
  name: 'WeeklySnapshot',

  components: {
    SummarySkeletonLoader,
    TableSkeleton,
    MetricsSummary,
    Pagination,
    WeeklySnapshotTable,
    ChartCard,
  },
  emits: ['track-page-load'],
  setup() {
    const { brandPrimary, green500 } = useColors();

    return { brandPrimary, green500 };
  },

  data() {
    return {
      canRightScroll: true,
    };
  },
  computed: {
    ...mapState(useAppLoading, ['pageLoading']),
    ...mapState(useAppPagination, ['page', 'totalPages']),
    ...mapState(useWeeklySnapshot, ['totals', 'labelsForLoading']),
    ...mapState(useFilters, ['mergedTempFilters', 'myJobsEnabled']),
    ...mapState(useDatePicker, ['dateWeekIndex']),
    ...mapState(useAppBase, ['browserBackOrForwardNavigation']),
    summaries() {
      const { brandPrimary, green500, totals } = this;

      return [
        {
          id: 'total-job-posts',
          label: 'Total Job Posts',
          value: numberWithCommas(Math.floor(totals.total_jobs_open)),
          color: brandPrimary,
        },
        {
          id: 'average-score',
          label: 'Average Score',
          value: numberWithCommas(Math.floor(totals.average_score)),
        },
        {
          id: 'average-days-live',
          label: 'Average Days Live',
          value: numberWithCommas(Math.floor(totals.average_days_published)),
          color: green500,
        },
        {
          id: 'total-revisions',
          label: 'Total Revisions',
          value: numberWithCommas(Math.floor(totals.total_revisions)),
          color: brandPrimary,
        },
      ];
    },
  },

  watch: {
    browserBackOrForwardNavigation(newValue) {
      /**
       * Verifies the correct path in case this is triggered
       * on a back button click to a different page
       */
      if (newValue && window.location.pathname === '/app/insights/weekly-snapshot') {
        const searchParams = getQueryParamsFromURL();

        this.loadPage({
          router: this.$router,
          page: searchParams?.page || 1,
          routeQuery: this.$route.query,
          sort: searchParams?.sort,
          selectedMyJobs: this.$route.query?.owned === 'true',
          dateWeekIndex: this.dateWeekIndex,
          mergedTempFilters: this.mergedTempFilters,
        });
      }
    },
    pageLoading: {
      handler(newValue) {
        if (!newValue) {
          nextTick(() => {
            const tableWrapper = document.querySelector('.v-table__wrapper');

            // Reset scroll listener on page change
            tableWrapper.removeEventListener('scroll', this.handleScroll);
            tableWrapper.addEventListener('scroll', this.handleScroll);
          });
        }
      },
    },
  },

  async mounted() {
    this.setShowInternalDefault(true);

    subscribeToFilterUpdates(this.loadPage);

    this.$emit('track-page-load', { page: 'Weekly Snapshot' });
    this.setFilterConfig({
      module: 'analytics/weeklySnapshot',
      datepicker: true,
      showMyJobs: true,
      subMenuType: null,
    });

    this.showFilterBarSettings({ route: this.$route.name, options: [] });

    this.setShareConfig({
      shareable: true,
    });

    this.setExportConfig({
      csvExport: true,
      pngExport: false,
      csvOptions: [
        {
          label: 'Data Table (.csv)',
          csvDownloadFunction: () => this.downloadWeeklySnapshot({
            document,
            mergedTempFilters: this.mergedTempFilters,
            dateWeekIndex: this.dateWeekIndex,
            myJobsEnabled: this.myJobsEnabled,
          }),
        },
      ],
    });

    await this.loadPage({
      router: this.$router,
      page: this.$route.query?.page || 1,
      routeQuery: this.$route.query,
      sort: this.$route.query?.sort,
      selectedMyJobs: this.$route.query?.owned === 'true',
      dateWeekIndex: this.dateWeekIndex || this.$route.query?.week,
      mergedTempFilters: this.mergedTempFilters,
    });

    this.setDatePickerVariant({ variant: 'week' });
  },

  beforeUnmount() {
    unsubscribeToFilterUpdates();

    this.clearPersistentFilters();
    this.setDatePickerVariant({ resetDateRange: true });
    this.resetSort();
    this.resetShareConfig();
    this.resetExportConfig();
  },

  methods: {
    ...mapActions(useWeeklySnapshot, ['loadPage', 'resetSort', 'downloadWeeklySnapshot']),
    ...mapActions(useFilters, ['sendFilterRequest', 'clearPersistentFilters', 'setFilterConfig']),
    ...mapActions(useDatePicker, ['setDatePickerVariant']),
    ...mapActions(useAppBase, ['showFilterBarSettings']),
    ...mapActions(useInsights, ['setShowInternalDefault']),
    ...mapActions(useInsightsExport, ['setExportConfig', 'resetExportConfig']),
    ...mapActions(useInsightsShare, ['setShareConfig', 'resetShareConfig']),
    handleScroll() {
      if (this.canRightScroll) {
        this.canRightScroll = false;
      }
    },
  },
};
</script>

<style scoped lang="less">
.card-text {
  position: relative;
}

.scroll-side {
  position: absolute;
  height: 100%;
  width: 250px;
  pointer-events: none;
}

.right-side-fade {
  right: 16px;
  background: linear-gradient(right, @white 10%, transparent 100%);
}

.fade-enter-active, .fade-leave-active {
  transition: opacity .5s;
}

.fade-enter, .fade-leave-to {
  opacity: 0;
}

.snapshot-card {
  border-radius: 8px;
  border: 1px solid @stroke-default;
  box-shadow: none;
  display: grid;
  grid-template-columns: minmax(0, 1fr);
}
</style>
